import {
  Action,
  VoiceChatMessage,
  buildWebsocketHeader,
  parseVoiceChatMessage,
} from "@/lib/messaging";
import { ModeType, Modes } from "@/lib/modes";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { toast } from "sonner";
import { usePocketBase } from "@/context/pocketbase-context";

/**
 * Hook to handle WebSocket message filtering and routing.
 *
 * Responsibilities:
 * - Create websocket connection and handle lifecycle
 * - Filter messages by current mode (ignore stale responses)
 * - Route translation vs main conversation messages
 * - Extract setup messages for mode context
 * - Track processing state
 *
 * Prevents race conditions where:
 * - User changes mode while server is processing previous request
 * - Server response arrives with old mode
 * - Messages get routed to wrong destination
 */

interface UseWebSocketMessagesReturn {
  websocketRef: MutableRefObject<WebSocket | null>;
  clearMessages: () => void;
  sendWebsocketMessage: (
    message: string | VoiceChatMessage,
    audio: Float32Array | undefined,
  ) => void;
}

/**
 * Filter and route WebSocket messages based on mode and action type.
 *
 * @param rawMessage - Raw message from WebSocket
 * @param currentMode - Current active mode
 *
 * @example
 * ```tsx
 * const {
 *   currentMessage,
 *   translationMessage,
 *   modeContext,
 *   isProcessing,
 *   markMessageSent,
 * } = useWebSocketMessages(wsInput, state.mode);
 *
 * // When sending audio
 * const handleSend = () => {
 *   sendAudio();
 *   markMessageSent(); // Sets isProcessing = true
 * };
 *
 * // Messages are automatically filtered and routed
 * <ModeSidePanelComponent message={currentMessage} />
 * <TranslationPanel message={translationMessage} />
 * ```
 */
export function useWebSocketMessages(
  currentMode: ModeType,
  setCurrentMessage: (message: VoiceChatMessage | undefined) => void,
  setTranslationMessage: (message: VoiceChatMessage | undefined) => void,
  setModeContext: (context: string) => void,
): UseWebSocketMessagesReturn {
  const websocketRef = useRef<WebSocket | null>(null);
  const [messageQueue, setMessageQueue] = useState<VoiceChatMessage[]>([]);
  const { token, session } = usePocketBase();

  // Create websocket connection on session available and set up message handler
  useEffect(() => {
    if (!session) {
      return;
    }

    const webSocketURL = new URL(
      import.meta.env.VITE_REACT_APP_VOICECHAT_URL_WS + "/v0/voicechat",
    );
    webSocketURL.searchParams.set("access_token", token);
    webSocketURL.searchParams.set("session", session);
    websocketRef.current = new WebSocket(webSocketURL);
    websocketRef.current.binaryType = "arraybuffer";

    websocketRef.current.onopen = (event) => {
      console.log("WebSocket connection opened:", event);
      toast.info("You're now connected to the voice chat server.");
    };
    websocketRef.current.onclose = (event) => {
      console.log("WebSocket closed:", event);
      toast.message("Disconnected from Voice Chat", {
        description: "The connection to the voice chat server was lost.",
      });
    };
    websocketRef.current.onerror = function (error) {
      if (error instanceof ErrorEvent) {
        console.error("WebSocket error:", error.message);
        toast.error("An error occurred with the voice chat connection");
      }
    };
    websocketRef.current.onmessage = (event) => {
      const responseIn = parseVoiceChatMessage(
        new Uint8Array(event.data as ArrayBuffer),
      );
      console.log("Parsed WebSocket message:", responseIn);
      setMessageQueue((prev) => [...prev, responseIn]);
    };

    // Cleanup on component unmount
    return () => {
      if (websocketRef.current) {
        console.log("Closing websocket connection");
        websocketRef.current.close();
      }
    };
  }, [session]);

  // Clear messages when mode changes
  useEffect(() => {
    setCurrentMessage(undefined);
    setModeContext("");
  }, [currentMode]);

  // Process incoming WebSocket messages
  useEffect(() => {
    if (messageQueue.length === 0) {
      return;
    }

    for (const rawMessage of messageQueue) {
      // Route 1: Translation mode messages
      if (rawMessage.mode === Modes.Translate.modeStr) {
        setTranslationMessage(rawMessage);
        continue;
      }

      // Route 2: Filter by mode (ignore messages from old mode)
      // This prevents race condition where mode changed but server sent old mode response
      if (rawMessage.mode !== currentMode.topLevelMode?.valueOf()) {
        console.log(
          `Ignoring message from mode ${rawMessage.mode} (current mode: ${currentMode.topLevelMode?.valueOf()})`,
        );
        continue;
      }

      // Route 3: Setup/context messages
      if (rawMessage.action === Action.Setup && rawMessage.message) {
        setModeContext(rawMessage.message);
        if (currentMode.modeStr === Modes.ImproTest.modeStr) {
          // For improtest we want to pass the setup message in the main panel as well since it contains the improv words
          setCurrentMessage(rawMessage);
        }
        continue;
      }

      // Route 4: Regular conversation messages
      setCurrentMessage(rawMessage);
    }

    setMessageQueue([]);
  }, [messageQueue, currentMode]);

  const clearMessages = useCallback(() => {
    setCurrentMessage(undefined);
    setTranslationMessage(undefined);
    setModeContext("");
  }, []);

  // To send plain strings or VoiceChatMessages with or without audio bytes attached
  const sendWebsocketMessage = useCallback(
    (message: string | VoiceChatMessage, audio: Float32Array | undefined) => {
      if (
        !websocketRef.current ||
        websocketRef.current.readyState !== WebSocket.OPEN
      ) {
        toast.error("WebSocket is not open. Unable to send message.");
        return;
      }

      let messageStr: string;
      if (typeof message === "string") {
        messageStr = message;
      } else {
        messageStr = buildWebsocketHeader(message);
      }

      console.log("Sending WS message: ", messageStr);
      if (audio) {
        const pcm16 = new Int16Array(audio.length);
        for (let i = 0; i < audio.length; i++) {
          const s = Math.max(-1, Math.min(1, audio[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        const messageWithAudio = new Blob([messageStr, pcm16]);
        websocketRef.current.send(messageWithAudio);
      } else {
        websocketRef.current.send(messageStr);
      }
    },
    [websocketRef],
  );

  return {
    websocketRef,
    clearMessages,
    sendWebsocketMessage,
  };
}
